import { AccountSummary, CatalogEntry } from '../services/reportsApi';
import { isPaddleConfigured } from './paddleLoader';

/**
 * What an account can buy right now. The single definition, shared by the
 * Account page and the zero-runs UI, so the upload card never promises a
 * purchase the Account page says isn't available.
 */
export interface PurchaseOptions {
  packs: CatalogEntry[];
  plans: CatalogEntry[];
  canPurchase: boolean;
  /**
   * A subscriber with no packs on offer: runs return at renewal, and nothing
   * is missing. Such a user must never be told purchasing is unavailable.
   */
  renewalOnly: boolean;
}

export function purchaseOptions(summary: AccountSummary): PurchaseOptions {
  const packs = summary.catalog.filter((entry) => entry.kind === 'one_time');
  // Hidden once a subscription exists. This is the ONLY enforcement of "one
  // Core subscription at a time", and deliberately so: opening a second
  // checkout creates a SECOND subscription, but rejecting the resulting
  // completed transaction server-side would mean taking a payment and
  // refusing the runs.
  const plans = summary.hasActiveSubscription
    ? []
    : summary.catalog.filter((entry) => entry.kind === 'subscription');
  const canPurchase = packs.length + plans.length > 0 && isPaddleConfigured();
  const renewalOnly = summary.hasActiveSubscription && packs.length === 0;
  return { packs, plans, canPurchase, renewalOnly };
}

/**
 * True when the account has nothing spendable. Used only to decide what to
 * show; the combined figure is never displayed (the two balances expire on
 * different schedules and are spent in a defined order).
 */
export function hasNoRuns(summary: AccountSummary): boolean {
  return summary.subscriptionRuns + summary.purchasedCredits === 0;
}
